import useSWR from 'swr'
import qs from 'query-string'
import {
  Category,
  CursorPaginatedResponse,
  FeedItem,
  GpsBounds,
  PaginatedResponse,
  Place,
  ResponseEnvelope,
  UserDetail,
  UserMe,
  UserSummary,
} from 'infra/type'
import { getRequest } from 'api/fetcher'
import { swrKeyHistory, SwrKeys } from 'api/swr-keys'

const useCustomSWR = <T>(path: string | null, params?: Record<string, any>) => {
  // should be cached with query params as a whole
  const wholePath =
    path === null ? null : `${path}${params ? `?${qs.stringify(params)}` : ''}`
  if (wholePath !== null) {
    swrKeyHistory.add(wholePath)
  }
  const { data: res, error } = useSWR<ResponseEnvelope<T>>(
    wholePath,
    getRequest,
  )
  return {
    data: res?.data,
    isLoading: !error && !res,
    isError: error,
  }
}

export const useMe = () => useCustomSWR<UserMe>('/users/me')

export const usePlaceList = (bounds?: GpsBounds) =>
  useCustomSWR<{
    places: Place[]
  }>(bounds ? SwrKeys.PLACE_LIST : null, {
    min_lat: bounds?.minLat,
    min_lng: bounds?.minLng,
    max_lat: bounds?.maxLat,
    max_lng: bounds?.maxLng,
  })

export const useCategoryList = () => useCustomSWR<Category[]>('/categories')

// TODO: replace useSWR
export const searchPlaceList = async (
  query: string | null,
  categories: number[] | null,
) => {
  const res: ResponseEnvelope<{ places: Place[] }> = await getRequest(
    `/places/search?${qs.stringify({ query, categories }, { skipNull: true })}`,
  )
  return res.data?.places || []
}

export const useCategoryPlaceList = (categories: number[] | null) =>
  useCustomSWR<{ places: Place[] }>(
    categories ? `/places/search?${qs.stringify({ categories })}` : null,
  )

export const usePlace = (placeId?: number) =>
  useCustomSWR<Place>(placeId ? `/places/${placeId}` : null)

export const useUserDetail = (userId?: number) =>
  useCustomSWR<UserDetail>(userId ? `/users/${userId}` : null)

export const useUserFollowers = (
  userId?: number,
  limit?: number,
  offset?: number,
) =>
  useCustomSWR<PaginatedResponse<UserSummary>>(
    userId ? `/users/${userId}/followers` : null,
    { limit, offset },
  )

export const useUserFollowings = (
  userId?: number,
  limit?: number,
  offset?: number,
) =>
  useCustomSWR<PaginatedResponse<UserSummary>>(
    userId ? `/users/${userId}/followings` : null,
    { limit, offset },
  )

export const useUserReportedPlaces = (
  userId?: number,
  limit?: number,
  offset?: number,
) =>
  useCustomSWR<PaginatedResponse<Place>>(
    userId ? `/users/${userId}/reported-places` : null,
    { limit, offset },
  )

// 커서 기반 피드. cursor 를 바꿔 다음 페이지를 가져오고, 컴포넌트에서 items 를 누적한다.
// nextCursor 가 null 이면 더 가져올 페이지가 없다.
export const useFeed = (limit?: number, cursor?: string) =>
  useCustomSWR<CursorPaginatedResponse<FeedItem>>('/feeds', { limit, cursor })
